import { useState } from 'react';

import type { Task } from '@/types/task';
import { TaskCategory } from '@/types/task';
import { getCategoryDisplayName } from '@/utils/category-utils';
import { deriveSingleStatus, findSoonestDuePredecessor, sortTasksInGroup } from '@/utils/prep-filter-utils';
import { ExpandChevron } from '@/components/common/ExpandChevron';
import { TaskRow } from '@/components/tasks/TaskRow';

interface TaskCategoryAccordionProps {
  /**
   * Category for this group. The display name is derived via getCategoryDisplayName.
   * This is the canonical enum value; null/undefined tasks already route to TaskCategory.Other
   * by the groupByCategory function before reaching this component.
   */
  category: TaskCategory;

  /** All tasks in this category group (all statuses, including done/snoozed/blocked). */
  tasks: Task[];

  /** Season id for TaskRow navigation links */
  seasonId: string;

  /** Full task map for predecessor title resolution */
  taskMap: Map<string, Task>;

  /** Today as ISO date string (YYYY-MM-DD) */
  todayStr: string;

  /**
   * Whether this group starts expanded.
   * The seed-once expand-state is managed by the parent (TaskListPage).
   */
  initialExpanded: boolean;

  /** Toggle-complete handler — same as TaskListPage's handleToggleComplete */
  onToggleComplete: (task: Task) => void;
}

/**
 * TaskCategoryAccordion — dynamic accordion group for the Prep task list (DASH-TD1 / M100 §3–5).
 *
 * Single classification: deriveSingleStatus feeds header {done}/{total}, blocked fold count,
 * and within-group sort. The button owns aria-expanded; ExpandChevron is decorative only.
 *
 * Blocked fold: nested "Blocked (N)" sub-fold (nested ExpandChevron, its button owns
 * its own aria-expanded). Predecessor resolved via findSoonestDuePredecessor — soonest-due
 * incomplete predecessor, tiebreak sortOrder then title (A3 assumption).
 */
export function TaskCategoryAccordion({
  category,
  tasks,
  seasonId,
  taskMap,
  todayStr,
  initialExpanded,
  onToggleComplete,
}: TaskCategoryAccordionProps) {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [blockedExpanded, setBlockedExpanded] = useState(false);

  const displayName = getCategoryDisplayName(category);

  // Counts derived from single classification (invariant: uses same path as header)
  const doneCount = tasks.filter((t) => deriveSingleStatus(t, todayStr) === 'done').length;
  const totalCount = tasks.length;

  // Separate blocked tasks from active tasks for the sub-fold
  const blockedTasks = tasks.filter((t) => deriveSingleStatus(t, todayStr) === 'blocked');
  const activeTasks = tasks.filter((t) => deriveSingleStatus(t, todayStr) !== 'blocked');

  // Sort active tasks: OVERDUE first → soonest dueDate → sortOrder
  const sortedActiveTasks = sortTasksInGroup(activeTasks, todayStr);

  return (
    <div data-testid={`category-group-${category}`} data-category={category}>
      {/* Group header button — owns aria-expanded */}
      <button
        type="button"
        aria-expanded={expanded}
        data-testid={`category-group-header-${category}`}
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-baseline justify-between text-left hover:bg-[var(--color-elevated)]/30 transition-colors duration-100"
        style={{ padding: '18px 16px 8px' }}
      >
        <span
          className="field-label"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {displayName}
        </span>
        <div className="flex items-center gap-1.5">
          <span
            data-testid={`category-group-count-${category}`}
            style={{
              fontSize: '10px',
              color: 'var(--color-text-muted)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {doneCount}/{totalCount}
          </span>
          <ExpandChevron
            expanded={expanded}
            size={12}
            className="text-[var(--color-text-muted)]"
          />
        </div>
      </button>

      {expanded && (
        <div data-testid={`category-group-body-${category}`}>
          {/* Active (non-blocked) task rows */}
          {sortedActiveTasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              seasonId={seasonId}
              onToggleComplete={onToggleComplete}
              showStrikethrough={false}
            />
          ))}

          {/* Blocked sub-fold (only rendered when there are blocked tasks) */}
          {blockedTasks.length > 0 && (
            <div data-testid={`blocked-fold-${category}`}>
              {/* Blocked fold header button — owns its own aria-expanded */}
              <button
                type="button"
                aria-expanded={blockedExpanded}
                data-testid={`blocked-fold-header-${category}`}
                onClick={() => setBlockedExpanded((v) => !v)}
                className="w-full flex items-center justify-between text-left"
                style={{ padding: '10px 16px 10px 20px' }}
              >
                <span
                  className="field-label"
                  style={{ color: 'var(--color-text-muted)', fontSize: '10px' }}
                  data-testid={`blocked-fold-label-${category}`}
                >
                  Blocked ({blockedTasks.length})
                </span>
                <ExpandChevron
                  expanded={blockedExpanded}
                  size={11}
                  className="text-[var(--color-text-muted)]"
                />
              </button>

              {blockedExpanded && (
                <div data-testid={`blocked-fold-body-${category}`}>
                  {blockedTasks.map((task) => {
                    // Soonest-due incomplete predecessor (A3: deterministic multi-predecessor rule)
                    const predecessor = findSoonestDuePredecessor(task, taskMap);
                    const predecessorTitle = predecessor?.title ?? null;

                    return (
                      <div
                        key={task.id}
                        data-testid={`blocked-task-${task.id}`}
                      >
                        <TaskRow
                          task={task}
                          seasonId={seasonId}
                          predecessorTitles={predecessorTitle ? [predecessorTitle] : []}
                          onToggleComplete={onToggleComplete}
                          showStrikethrough={false}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
